import type { TokenResponseExtended as TokenResponse } from 'owa-service/lib/tokenResponseExtended';
import type { ConsumerTokenRequestParams } from 'owa-tokenprovider-utils';
import { parse, stringify } from 'querystring';
import type TokenResponseCode from 'owa-service/lib/contract/TokenResponseCode';
import addSeconds from 'owa-date-utc-fn/lib/addSeconds';
import type { TokenPerformanceLogger } from './TokenPerformanceLogger';

const eventListeners: {
    [key: string]: (e: any) => void;
} = {};

export async function fetchTokenFromMSA(
    perfLog: TokenPerformanceLogger,
    consumerParams: ConsumerTokenRequestParams
): Promise<TokenResponse> {
    const iframeId = encodeURIComponent(
        consumerParams.client_id + consumerParams.scope + consumerParams.requestId
    );
    let tokenResponse: TokenResponse = {};
    try {
        tokenResponse = await internalFetchTokenFromMSA(perfLog, consumerParams, self, iframeId);
    } catch (e) {
        // Re-throw so the error propagates to the base class failure lifecycle.
        throw e instanceof Error ? e : new Error('MSA token fetch failed');
    } finally {
        cleanUpIframe(iframeId);
    }
    return tokenResponse;
}

async function internalFetchTokenFromMSA(
    perfLog: TokenPerformanceLogger,
    consumerParams: ConsumerTokenRequestParams,
    parentWindow: Window,
    iframeId: string
): Promise<TokenResponse> {
    const url = getAuthenticationUrl(consumerParams);
    if (!parentWindow.document.getElementById(iframeId)) {
        const iframe = parentWindow.document.createElement('iframe');
        iframe.src = url.toString();
        iframe.style.display = 'none';
        iframe.id = iframeId;
        parentWindow.document.body.appendChild(iframe);
        return new Promise((resolve, reject) => {
            eventListeners[iframeId] = function () {
                let tokenResponse: TokenResponse = {};
                try {
                    if (
                        iframe.contentWindow &&
                        iframe.contentWindow.location.host == parentWindow.location.host
                    ) {
                        tokenResponse = parseHashParam(
                            perfLog,
                            iframe.contentWindow.location.hash.substring(1)
                        );
                        perfLog.addCheckpoint('TokenRetrievedFromMSASuccess');
                    }
                } catch {
                    tokenResponse.TokenResultCode = 2;
                    /* oxlint-disable-next-line prefer-promise-reject-errors -- (https://aka.ms/OWALintWiki)
                     * Promise reject must be an Error object. */
                    reject(tokenResponse);
                }
                resolve(tokenResponse);
            };
            iframe.addEventListener('load', eventListeners[iframeId], false);
        });
    }

    return { TokenResultCode: 2 };
}

function getSingleValue(queryParam: string | string[] | undefined): string | undefined {
    return Array.isArray(queryParam) ? queryParam[0] : queryParam;
}

export function parseHashParam(perfLog: TokenPerformanceLogger, hashParam: string): TokenResponse {
    const params = parse(hashParam);
    const tokenResponse: TokenResponse = {};
    if (params.access_token) {
        tokenResponse.AccessToken = getSingleValue(params.access_token);
        tokenResponse.AccessTokenExpiry = addSeconds(
            Date.now(),
            parseInt(getSingleValue(params.expires_in) ?? '0')
        ).toISOString();
        tokenResponse.TokenResultCode = 0;
    } else if (
        params.error == 'login_required' ||
        params.error == 'interaction_required' ||
        params.error == 'consent_required'
    ) {
        perfLog.addCustomProperty('msaError', params.error as string);
        tokenResponse.TokenResultCode = 3;
    } else {
        perfLog.addCheckpoint('Unknown_Error');
        tokenResponse.TokenResultCode = 2;
    }

    return tokenResponse;
}

function cleanUpIframe(id: string) {
    var element = self.document.getElementById(id);
    if (element != null) {
        element.removeEventListener('load', eventListeners[id]);
        self.document.body.removeChild(element);
        delete eventListeners[id];
    }
}

export function getAuthenticationUrl(consumerParams: ConsumerTokenRequestParams): string {
    const queryParams = {
        response_type: 'token',
        prompt: 'none', // silent auth, don't prompt the user
        redirect_uri: consumerParams.redirect_uri,
        scope: consumerParams.scope,
        client_id: consumerParams.client_id,
    };

    return consumerParams.authorization_uri + '?' + stringify(queryParams);
}
